import React, { useState, useEffect } from 'react';
import { API_BASE } from '../services/apiService';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, ArrowRight, Edit3, Download, Save, Home, SkipForward } from 'lucide-react';

const PolicyExtractorPage = () => {
  const [mode, setMode] = useState('choose'); // choose, upload, manual, result
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [fields, setFields] = useState({});
  const [fieldDefs, setFieldDefs] = useState({ sections: {}, sectionOrder: [] });
  const [clientName, setClientName] = useState('');
  const [policyId, setPolicyId] = useState('');
  const [editMode, setEditMode] = useState(false);

  // Load field definitions on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/policy/fields`).then(r => r.json()).then(d => {
      setFieldDefs(d);
      // Initialize empty fields
      const empty = {};
      Object.values(d.sections || {}).flat().forEach(f => {
        empty[f.key] = { label: f.label, section: '', raw: '', formatted: '', question: f.question };
      });
      setFields(empty);
    }).catch(() => {});
  }, []);

  const handleUploadPDF = async () => {
    if (!file) { setError('Please select a PDF file'); return; }
    setLoading(true); setError(''); setStatusMsg('Uploading and extracting...');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/api/policy/extract-pdf`, { method: 'POST', body: form });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      if (data.fields) {
        setFields(data.fields);
        setMode('result');
        setEditMode(true);
      }
    } catch (e) { setError('Upload failed: ' + e.message); }
    setLoading(false);
  };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/policy/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientName, fields }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); }
      else { setPolicyId(data.policyId); setEditMode(false); }
    } catch (e) { setError('Save failed: ' + e.message); }
    setLoading(false);
  };

  const handleExportCSV = () => {
    const rows = [['Key Fields', 'Formatted Output']];
    Object.entries(fields).forEach(([key, val]) => {
      rows.push([val.label || key, val.formatted || val.raw || '']);
    });
    const csv = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${clientName || 'policy'}_extracted_data.csv`;
    a.click();
  };

  const updateField = (key, value) => {
    setFields(prev => ({
      ...prev,
      [key]: { ...prev[key], formatted: value, raw: value },
    }));
  };

  const inputCls = "w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 focus:border-purple-400";

  const sectionColors = {
    'Sum Insured': 'bg-purple-600',
    'Family Structure': 'bg-purple-600',
    'Maternity': 'bg-pink-500',
    'Room Rent': 'bg-red-500',
    'CoPay': 'bg-red-500',
    'OPD': 'bg-orange-500',
    'Other GHI': 'bg-violet-600',
    'Cancer Care': 'bg-rose-600',
    'Disease Capping': 'bg-fuchsia-600',
    'Policy Info': 'bg-gray-600',
  };

  // ===== CHOOSE MODE =====
  if (mode === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50">
        <div className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => window.location.href = '/'} className="p-1.5 rounded-lg hover:bg-purple-50 transition-colors" title="Home">
                <Home size={16} className="text-purple-600" />
              </button>
              <img src="/pazcare-logo.svg" alt="PazCare" className="h-8" />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">Policy Document Extractor</span>
              <button onClick={() => window.location.href = '/'} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <SkipForward size={12} /> Skip
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-16">
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">Extract Policy Benefits</h1>
          <p className="text-sm text-gray-500 text-center mb-10">Upload your policy PDF or enter details manually</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Upload PDF */}
            <div
              className="bg-white rounded-2xl border-2 border-gray-200 p-8 text-center cursor-pointer hover:border-purple-400 hover:shadow-lg transition-all"
              onClick={() => setMode('upload')}
            >
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Upload size={24} className="text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Upload Policy PDF</h3>
              <p className="text-xs text-gray-500">AI extracts all key fields automatically from your policy document</p>
            </div>

            {/* Manual Entry */}
            <div
              className="bg-white rounded-2xl border-2 border-gray-200 p-8 text-center cursor-pointer hover:border-pink-400 hover:shadow-lg transition-all"
              onClick={() => { setMode('result'); setEditMode(true); }}
            >
              <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Edit3 size={24} className="text-pink-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Enter Manually</h3>
              <p className="text-xs text-gray-500">Fill in policy benefit fields yourself if you don't have a PDF</p>
            </div>
          </div>

          <div className="text-center mt-8 text-[10px] text-purple-600">
            With us, your <span className="font-bold text-pink-500">data</span> is safe, stays <span className="font-bold text-pink-500">anonymous</span>,<br/>and personal details like names are <span className="font-bold text-pink-500">masked</span> in reports.
          </div>
        </div>
      </div>
    );
  }

  // ===== UPLOAD MODE =====
  if (mode === 'upload') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <button onClick={() => window.location.href = '/'} className="p-1.5 rounded-lg hover:bg-purple-50" title="Home">
                <Home size={16} className="text-purple-600" />
              </button>
              <img src="/pazcare-logo.svg" alt="PazCare" className="h-8" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Upload Policy Document</h2>
            <p className="text-xs text-gray-500 mt-1">PDF format — we'll extract all key benefit fields</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                <AlertCircle size={14} className="text-red-500" />
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Company Name</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} className={inputCls} placeholder="e.g. Paz Corp" />
            </div>

            <div
              className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-all mb-4"
              onClick={() => document.getElementById('policy-pdf-input').click()}
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText size={18} className="text-purple-500" />
                  <span className="text-sm text-gray-700 font-medium">{file.name}</span>
                  <button onClick={e => { e.stopPropagation(); setFile(null); }} className="text-xs text-gray-400 hover:text-red-500 ml-1">x</button>
                </div>
              ) : (
                <>
                  <Upload size={28} className="mx-auto mb-2 text-purple-400" />
                  <p className="text-sm text-gray-600 mb-1">Upload Policy PDF</p>
                  <p className="text-[10px] text-gray-400">Max 20MB</p>
                </>
              )}
              <input id="policy-pdf-input" type="file" accept=".pdf" className="hidden" onChange={e => setFile(e.target.files[0])} />
            </div>

            <button
              onClick={handleUploadPDF}
              disabled={loading || !file}
              className="w-full py-3 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {loading ? statusMsg : 'Extract Policy Data'}
            </button>

            <div className="flex justify-between mt-3">
              <button onClick={() => setMode('choose')} className="text-xs text-gray-400 hover:text-gray-600">← Back</button>
              <button onClick={() => { setMode('result'); setEditMode(true); }} className="text-xs text-purple-600 hover:text-purple-800">Enter manually instead →</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== RESULT / MANUAL MODE =====
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => window.location.href = '/'} className="p-1.5 rounded-lg hover:bg-purple-50" title="Home">
              <Home size={16} className="text-purple-600" />
            </button>
            <img src="/pazcare-logo.svg" alt="PazCare" className="h-7" />
            <span className="text-sm font-bold text-gray-800">Policy Benefits — {clientName || 'Untitled'}</span>
            {policyId && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Saved: {policyId}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditMode(!editMode)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
              <Edit3 size={12} /> {editMode ? 'View Mode' : 'Edit'}
            </button>
            <button onClick={handleExportCSV} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
              <Download size={12} /> Export CSV
            </button>
            <button onClick={handleSave} disabled={loading} className="flex items-center gap-1 px-4 py-1.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <AlertCircle size={14} className="text-red-500" />
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* Client Name */}
        <div className="mb-4">
          <input
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            className="text-lg font-bold text-gray-900 border-b-2 border-transparent hover:border-purple-200 focus:border-purple-500 focus:outline-none px-1 py-1 bg-transparent"
            placeholder="Enter Company Name"
          />
        </div>

        {/* Fields by Section */}
        {(fieldDefs.sectionOrder || []).map(section => {
          const sectionFields = (fieldDefs.sections || {})[section] || [];
          if (sectionFields.length === 0) return null;

          return (
            <div key={section} className="mb-6">
              {/* Section header */}
              <div className={`${sectionColors[section] || 'bg-purple-600'} text-white text-sm font-bold px-4 py-2 rounded-t-lg`}>
                {section}
              </div>

              {/* Fields table */}
              <div className="bg-white border border-gray-200 rounded-b-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-[11px] font-semibold text-gray-600 px-4 py-2 w-1/4">Metric</th>
                      <th className="text-left text-[11px] font-semibold text-gray-600 px-4 py-2">Client's Current Benefits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionFields.map(f => {
                      const val = fields[f.key] || {};
                      return (
                        <tr key={f.key} className="border-b border-gray-100 hover:bg-purple-50/30">
                          <td className="px-4 py-2">
                            <span className="text-xs font-medium text-gray-700">{f.label}</span>
                          </td>
                          <td className="px-4 py-2">
                            {editMode ? (
                              <input
                                value={val.formatted || val.raw || ''}
                                onChange={e => updateField(f.key, e.target.value)}
                                className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-purple-300 focus:border-purple-400"
                                placeholder={`Enter ${f.label}`}
                              />
                            ) : (
                              <span className="text-xs text-gray-800">{val.formatted || val.raw || <span className="text-gray-400">-</span>}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PolicyExtractorPage;
